/**
 * The required File / Folder choice for an ambiguous share path (plan Task 7.4, S7-10). A share SAS covers every file
 * and folder under the share, so a pasted `…/share/report.csv` could name either; main probes it
 * (`azure-storage:classify-share-path`) and the answer preselects the choice. When the probes cannot tell, the choice
 * stays unselected with the reason, and Test / Save refuse until the user picks (`NewConnectionPopup`).
 */
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { Connection } from '@/types/electron';
import type { ConnectionFormValues } from './connectionFormSchema';
import { chosenSharePathScope, SHARE_PATH_REQUIRED, withoutTransientStorageFields, type SharePathKind } from './azureStorageSharePath';
import { FormRow } from '../connection-dialog/pane/FormRow';
import { CHECKBOX_CLASS, type ConnectionFormApi } from '../connection-dialog/fields/fieldControls';
type Classification = { state: 'idle' } | { state: 'checking' } | { state: 'unknown'; reason: string };

const CHOICES: ReadonlyArray<{ kind: SharePathKind; label: string }> = [
  { kind: 'file', label: 'File' },
  { kind: 'directory', label: 'Folder' },
];

/** Classifies the unsaved config while the choice is unset; a file / directory answer is applied through `apply`. */
function useSharePathClassification(values: Partial<ConnectionFormValues>, active: boolean, apply: (kind: SharePathKind) => void): Classification {
  const [classification, setClassification] = useState<Classification>({ state: 'idle' });
  const scope = values.azureStorageScope;
  const key = JSON.stringify([values.server, values.password, values.authentication, scope]);
  useEffect(() => {
    const api = window.electronAPI?.azureStorage;
    if (!active || !api?.classifySharePath) { setClassification({ state: 'idle' }); return undefined; }
    const requestId = uuidv4();
    let current = true;
    setClassification({ state: 'checking' });
    const config = { id: 'share-path-check', ...withoutTransientStorageFields(values) } as Connection;
    void api.classifySharePath({ config, requestId, timeoutMs: 15_000 }).then((answer) => {
      if (!current) return;
      if (answer.ok && answer.value.kind !== 'unknown') { setClassification({ state: 'idle' }); apply(answer.value.kind); }
      else setClassification({ state: 'unknown', reason: answer.ok && answer.value.kind === 'unknown' ? answer.value.reason : answer.ok ? '' : answer.error.message });
    }).catch(() => { if (current) setClassification({ state: 'unknown', reason: 'The check did not answer.' }); });
    return () => { current = false; void api.cancel?.({ requestId }).catch(() => undefined); };
    // `key` covers every value the probe reads; `values` / `apply` change identity on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key]);
  return classification;
}

export function AzureStorageSharePathChoice({ form, values }: { form: ConnectionFormApi; values: Partial<ConnectionFormValues> }) {
  const { track } = useAnalytics();
  const choice = values.azureStorageShareChoice;
  const apply = (kind: SharePathKind) => {
    form.setFieldValue('azureStorageScope', chosenSharePathScope(form.state.values.azureStorageScope, kind));
    form.setFieldValue('azureStorageShareChoice', kind);
  };
  const classification = useSharePathClassification(values, choice === 'unset', apply);
  if (choice === undefined) return null;
  return (
    <FormRow row="azureStorageShareChoice" label="The path is a" groupId="storage-share-path-label" testId="storage-share-path-choice">
      <div className="flex min-h-9 flex-wrap items-center gap-x-5 gap-y-1" role="radiogroup" aria-labelledby="storage-share-path-label">
        {CHOICES.map((option) => (
          <label key={option.kind} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="storage-share-path-kind"
              className={CHECKBOX_CLASS}
              checked={choice === option.kind}
              onChange={() => { apply(option.kind); track('ui:storage_form_share_path', { kind: option.kind }); }}
              data-testid={`storage-share-path-${option.kind}`}
            />
            {option.label}
          </label>
        ))}
      </div>
      {choice === 'unset' && classification.state === 'checking' && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="storage-share-path-status">Checking whether this path is a file or a folder…</p>
      )}
      {choice === 'unset' && classification.state !== 'checking' && (
        <p className="mt-1 text-xs text-destructive" data-testid="storage-share-path-required">
          {SHARE_PATH_REQUIRED}
          {classification.state === 'unknown' && classification.reason ? ` ${classification.reason}` : ''}
        </p>
      )}
    </FormRow>
  );
}
